// src/App.js
import React, { useEffect } from 'react';
import {
  ThemeProvider,
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import './i18n';
import PasswordRequirements from './PasswordRequirements';
import theme, { tokens } from './theme';
import AuthorityPortal from './AuthorityPortal';



const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function LanguageSelector() {
  const { i18n } = useTranslation();
  const handleChange = (e) => {
    i18n.changeLanguage(e.target.value);
  };
  return (
    <div style={{ position: 'absolute', top: 10, right: 10 }}>
      <select value={i18n.language} onChange={handleChange} style={{ padding: '5px', fontSize: '0.9rem' }}>
        <option value="tr">🇹🇷 Türkçe</option>
        <option value="en">🇺🇸 English</option>
        <option value="ar">🇸🇦 العربية</option>
        <option value="fa">🇮🇷 فارسی</option>
        <option value="ru">🇷🇺 Русский</option>
      </select>
    </div>
  );
}

function StepTrail({ step, mode, t }) {
  // Akış gerçekten iki adımlı: önce kimlik, sonra yeni şifre.
  // Mevcut şifre yolunda tek adım var, o yüzden gösterilmez.
  if (mode === 'current') return null;
  const steps = [t('stepVerify'), t('stepNewPassword')];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, justifyContent: 'center' }}>
      {steps.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <React.Fragment key={label}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700,
                backgroundColor: done || active ? tokens.brand : tokens.canvas,
                color: done || active ? '#fff' : tokens.inkFaint,
                border: `1px solid ${done || active ? tokens.brand : tokens.line}`,
              }}>
                {done ? '✓' : i + 1}
              </Box>
              <Typography sx={{
                fontSize: '0.85rem',
                fontWeight: active ? 700 : 400,
                color: active ? tokens.ink : tokens.inkMuted,
              }}>
                {label}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{ width: 32, height: 1, backgroundColor: done ? tokens.brand : tokens.line }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}



function ChoiceCard({ icon: Icon, title, subtitle, onClick }) {
  return (
    <Card
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      sx={{
        cursor: 'pointer', height: '100%',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover, &:focus-visible': {
          transform: 'translateY(-2px)',
          borderColor: tokens.brand,
          boxShadow: '0 2px 4px rgba(18,35,46,0.06), 0 16px 32px -20px rgba(12,132,204,0.55)',
        },
        '&:focus-visible': { outline: `2px solid ${tokens.brand}`, outlineOffset: 2 },
        '&:hover .choice-icon': { backgroundColor: tokens.brand, color: '#fff' },
      }}
    >
      <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2.5 }}>
        <Box className="choice-icon" sx={{
          width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: tokens.brandWash, color: tokens.brand,
          transition: 'background-color 160ms ease, color 160ms ease',
        }}>
          <Icon sx={{ fontSize: 26 }} />
        </Box>
        <Box sx={{ textAlign: 'left', minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: tokens.ink, mb: 0.25 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>{subtitle}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <Container maxWidth="md" sx={{ pt: { xs: 3, sm: 6 }, pb: 6 }}>
      <LanguageSelector />

      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <a href="https://sifre.lokmanhekim.edu.tr">
          <img
            src={process.env.PUBLIC_URL + '/assets/icon/logo.png'}
            alt={t('logoAlt')}
            style={{ width: '240px' }}
          />
        </a>
        <Typography variant="body1" sx={{ color: tokens.inkMuted, mt: 2, maxWidth: 520, mx: 'auto' }}>
          {t('infoText1')}
        </Typography>
      </Box>

      {/* Eylem önce gelir: kullanıcı buraya şifresini değiştirmeye geliyor. */}
      <Grid container spacing={2.5} justifyContent="center" sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <ChoiceCard
            icon={SchoolIcon}
            title={t('landingStudentTitle')}
            subtitle={t('landingStudentSubtitle')}
            onClick={() => navigate('/ogrenci')}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ChoiceCard
            icon={BusinessCenterIcon}
            title={t('landingPersonnelTitle')}
            subtitle={t('landingPersonnelSubtitle')}
            onClick={() => navigate('/personel')}
          />
        </Grid>
      </Grid>

      {/* Bilgilendirme referans metnidir; eylemin önüne geçmemeli. */}
      <Box sx={{
        backgroundColor: tokens.surface, border: `1px solid ${tokens.line}`,
        borderLeft: `3px solid ${tokens.brand}`, borderRadius: 2, p: 2.5,
      }}>
        <Typography variant="body2" sx={{ color: tokens.inkMuted, whiteSpace: 'pre-line' }}
                    dangerouslySetInnerHTML={{ __html: t('warningText') }} />
        <Typography variant="body2" sx={{ color: tokens.inkMuted, whiteSpace: 'pre-line', mt: 1 }}
                    dangerouslySetInnerHTML={{ __html: t('warningText2') }} />
      </Box>

      <Box sx={{ textAlign: 'center', pt: 3, mt: 4, borderTop: `1px solid ${tokens.line}` }}>
        <Typography variant="caption" sx={{ color: tokens.inkFaint }}>{t('footer')}</Typography>
      </Box>
    </Container>
  );
}

function FormPage({ userType }) {
  const { t } = useTranslation();
  const [verifyData, setVerifyData] = React.useState({ username: '', tc_no: '', phone: '' });
  const [userInfo, setUserInfo] = React.useState({ name: '', surname: '' });
  const [verificationCode, setVerificationCode] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = React.useState('');
  const [resultMessage, setResultMessage] = React.useState('');
  const [showChangePassword, setShowChangePassword] = React.useState(false);
  const [captchaToken, setCaptchaToken] = React.useState('');
  // Yükleniyor durumları: butonlar bunlarla devre dışı bırakılır.
  // Eskiden hiçbir koruma yoktu; kullanıcılar üst üste tıklıyor ve her tık
  // ayrı bir istek üretiyordu (hata kayıtlarının üçte ikisi bu yüzden oluşmuş).
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [isChanging, setIsChanging] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [toast, setToast] = React.useState('');
  // Doğrulama yöntemi: 'sms' (varsayılan), 'email' (telefonu güncel olmayanlar için),
  // 'current' (mevcut şifresini bilenler - SMS'e hiç gerek yok).
  const [verifyMode, setVerifyMode] = React.useState('sms');
  const [emailAvailable, setEmailAvailable] = React.useState(false);
  const [forbidTc, setForbidTc] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);

  useEffect(() => {
    // E-posta ile doğrulama sunucuda yapılandırılmadıysa seçenek gösterilmez.
    fetch(`${API_BASE_URL}/verification-channels/`)
      .then((r) => r.json())
      .then((d) => setEmailAvailable(Boolean(d && d.email)))
      .catch(() => setEmailAvailable(false));

    // Gereksinim listesi sunucudaki politikadan okunur; kural iki yerde ayrı
    // ayrı tanımlı kalmasın diye (PASSWORD_FORBID_TC ayarı gibi).
    fetch(`${API_BASE_URL}/password-rules/`)
      .then((r) => r.json())
      .then((d) => setForbidTc(Boolean(d && d.forbid_tc)))
      .catch(() => setForbidTc(false));
  }, []);

  const handleChangeWithCurrent = async (e) => {
    e.preventDefault();
    if (isChanging) return;
    if (newPassword !== newPasswordConfirm) {
      setResultMessage(t('errorPasswordMismatch'));
      return;
    }
    setIsChanging(true);
    try {
      const response = await fetch(`${API_BASE_URL}/change-password-with-current/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: verifyData.username,
          current_password: currentPassword,
          new_password: newPassword,
          user_type: userType
        })
      });
      const data = await response.json();
      if (data.message) {
        navigate('/success');
      } else if (data.error) {
        setResultMessage(data.error);
      } else if (data.detail) {
        setResultMessage(data.detail);
      }
    } catch (error) {
      setResultMessage(t('errorFetch') + error.message);
    } finally {
      setIsChanging(false);
    }
  };
  const navigate = useNavigate();

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleVerifyUser = async (e) => {
    e.preventDefault();
    if (isVerifying) return; // çift gönderim koruması
    // reCAPTCHA yalnızca SMS yolunda gösteriliyor; e-posta yolunda istenmez.
    if (verifyMode === 'sms' && !captchaToken) {
      setResultMessage("Lütfen 'Ben robot değilim' kutucuğunu işaretleyin.");
      return;
    }
    setIsVerifying(true);
    try {
      // E-posta yolunda telefon sorulmaz; kod AD'de kayıtlı adrese gider.
      const isEmail = verifyMode === 'email';
      const endpoint = isEmail ? '/verify-user-email/' : '/verify-user/';
      const payload = isEmail
        ? {
            username: verifyData.username,
            tc_no: verifyData.tc_no,
            user_type: userType
          }
        : { ...verifyData, user_type: userType, captchaToken };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.detail) {
        // FastAPI HTTPException gövdesi
        setResultMessage(data.detail);
        return;
      }
      if (data.message) {
        setResultMessage(data.message);
        setShowChangePassword(true);
        // Kullanıcı bilgilerini sakla (isim ve soyisim)
        if (data.name || data.surname) {
          setUserInfo({
            name: data.name || '',
            surname: data.surname || ''
          });
        }
      } else if (data.error) {
        setResultMessage(data.error);
      } else {
        setResultMessage(JSON.stringify(data, null, 2));
      }
    } catch (error) {
      setResultMessage(t('errorFetch') + error.message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleGeneratePassword = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      // Şifre sunucuda üretilir; kurallar tek kaynaktan (password_policy.py)
      // geldiği için üretilen şifrenin AD ve Google tarafından kabul edilmesi garanti.
      const response = await fetch(`${API_BASE_URL}/generate-password/`);
      const data = await response.json();
      if (data.password) {
        setNewPassword(data.password);
        setNewPasswordConfirm(data.password);
        setShowPassword(true);
        setShowPasswordConfirm(true);
        try {
          await navigator.clipboard.writeText(data.password);
          setToast(t('toastGenerated'));
        } catch {
          setToast(t('toastGeneratedNoCopy'));
        }
      }
    } catch (error) {
      setResultMessage(t('errorFetch') + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (isChanging) return; // çift gönderim koruması
    if (newPassword !== newPasswordConfirm) {
      setResultMessage(t('errorPasswordMismatch'));
      return;
    }
    setIsChanging(true);
    try {
      const payload = {
        username: verifyData.username,
        new_password: newPassword,
        verification_code: verificationCode,
        user_type: userType
      };
      const response = await fetch(`${API_BASE_URL}/change-password/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.message) {
        navigate("/success");
      } else if (data.error) {
        setResultMessage(data.error);
      } else {
        setResultMessage(JSON.stringify(data, null, 2));
      }
    } catch (error) {
      setResultMessage(t('errorFetch') + error.message);
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <LanguageSelector />
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <a href="https://sifre.lokmanhekim.edu.tr">
          <img
            src={process.env.PUBLIC_URL + '/assets/icon/logo.png'}
            alt={t('logoAlt')}
            style={{ width: '270px', marginBottom: '1rem' }}
          />
        </a>
        <Typography variant="h5" sx={{ color: tokens.ink }}>
          {userType === 'ogrenci' ? t('formPageTitleStudent') : t('formPageTitlePersonnel')}
        </Typography>
      </Box>

      <StepTrail step={showChangePassword ? 1 : 0} mode={verifyMode} t={t} />


      <Box sx={{
          backgroundColor: tokens.surface,
          p: { xs: 2, sm: 3 },
          borderRadius: 2.5,
          mb: 3,
          border: `1px solid ${tokens.line}`,
          boxShadow: '0 1px 2px rgba(18,35,46,0.04), 0 8px 24px -16px rgba(18,35,46,0.25)'
        }}>
        <Typography variant="h6" color="primary" mb={2}>
          {t('verificationSection')}
        </Typography>

        {/* Kimlik doğrulama yöntemi seçimi */}
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('verifyMethodQuestion')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant={verifyMode === 'sms' ? 'contained' : 'outlined'}
            onClick={() => { setVerifyMode('sms'); setShowChangePassword(false); setResultMessage(''); }}
            sx={verifyMode === 'sms' ? { color: 'white' } : {}}
          >
            {t('verifyMethodSms')}
          </Button>
          {emailAvailable && (
            <Button
              size="small"
              variant={verifyMode === 'email' ? 'contained' : 'outlined'}
              onClick={() => { setVerifyMode('email'); setShowChangePassword(false); setResultMessage(''); }}
              sx={verifyMode === 'email' ? { color: 'white' } : {}}
            >
              {t('verifyMethodEmail')}
            </Button>
          )}
          <Button
            size="small"
            variant={verifyMode === 'current' ? 'contained' : 'outlined'}
            onClick={() => { setVerifyMode('current'); setShowChangePassword(false); setResultMessage(''); }}
            sx={verifyMode === 'current' ? { color: 'white' } : {}}
          >
            {t('verifyMethodCurrent')}
          </Button>
        </Box>

        {verifyMode === 'current' ? (
          <form onSubmit={handleChangeWithCurrent}>
            <Typography variant="body2" sx={{ color: 'gray', mb: 2 }}>
              {t('currentPasswordHint')}
            </Typography>
            <TextField
              label={userType === 'ogrenci' ? t('labelUsernameStudent') : t('labelUsernamePersonnel')}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={verifyData.username}
              onChange={(e) => setVerifyData({ ...verifyData, username: e.target.value })}
              required
            />
            <TextField
              label={t('labelCurrentPassword')}
              type={showCurrentPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              label={t('labelNewPassword')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{ mb: 1 }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleGeneratePassword}
              disabled={isGenerating}
              startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
              sx={{ mb: 2 }}
            >
              {t('buttonGeneratePassword')}
            </Button>
            <PasswordRequirements
              password={newPassword}
              username={verifyData.username}
              tcNo={verifyData.tc_no}
              showTc={forbidTc}
            />
            <TextField
              label={t('labelNewPasswordConfirm')}
              type={showPasswordConfirm ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              required
              error={newPasswordConfirm.length > 0 && newPassword !== newPasswordConfirm}
              helperText={
                newPasswordConfirm.length > 0 && newPassword !== newPasswordConfirm
                  ? t('passwordsDoNotMatch')
                  : ' '
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} edge="end">
                      {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isChanging}
              startIcon={isChanging ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ fontWeight: 'bold', color: 'white' }}
            >
              {isChanging ? t('changingInProgress') : t('buttonChangePassword')}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyUser}>
            <TextField
              label={userType === 'ogrenci' ? t('labelUsernameStudent') : t('labelUsernamePersonnel')}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={verifyData.username}
              onChange={(e) => setVerifyData({ ...verifyData, username: e.target.value })}
              required
            />
            <TextField
              label={t('labelTC')}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={verifyData.tc_no}
              onChange={(e) => setVerifyData({ ...verifyData, tc_no: e.target.value })}
              required
            />

            {verifyMode === 'sms' ? (
              <>
                <Typography variant="body2" sx={{ color: 'red', fontSize: '0.8rem', mb: 1 }}>
                  {t('phoneWarning')}
                </Typography>
                <TextField
                  label={t('labelPhone')}
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={verifyData.phone}
                  onChange={(e) => setVerifyData({ ...verifyData, phone: e.target.value })}
                  required
                />
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <ReCAPTCHA
                    sitekey="6LepwecqAAAAAOspPry5PXpVvMgETIXkXVDLCVKa"
                    onChange={handleCaptchaChange}
                    size="normal"
                  />
                </Box>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'gray', mb: 2 }}>
                {t('emailCodeHint')}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isVerifying}
              startIcon={isVerifying ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ fontWeight: 'bold', color: 'white' }}
            >
              {isVerifying ? t('verifyingInProgress') : t('buttonVerify')}
            </Button>
          </form>
        )}
      </Box>

      {showChangePassword && (
        <Box sx={{
            backgroundColor: '#fff',
            p: 2,
            borderRadius: 2,
            boxShadow: 1,
            mb: 4,
            border: '2px solid #0c84cc'
          }}>
          <Typography variant="h6" color="primary" mb={2}>
            {t('changePasswordSection')}
          </Typography>
          <form onSubmit={handleChangePassword}>
            <TextField
              label={t('labelVerificationCode')}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
            />
            <TextField
              label={t('labelNewPassword')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{ mb: 1 }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPassword ? t('tooltipHidePassword') : t('tooltipShowPassword')}>
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleGeneratePassword}
                disabled={isGenerating}
                startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
              >
                {t('buttonGeneratePassword')}
              </Button>
              {newPassword && (
                <Tooltip title={t('tooltipCopyPassword')}>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(newPassword);
                        setToast(t('toastCopied'));
                      } catch {
                        setToast(t('toastCopyFailed'));
                      }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <PasswordRequirements
              password={newPassword}
              username={verifyData.username}
              name={userInfo.name}
              surname={userInfo.surname}
              tcNo={verifyData.tc_no}
              showTc={forbidTc}
            />
            <TextField
              label={t('labelNewPasswordConfirm')}
              type={showPasswordConfirm ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              required
              error={newPasswordConfirm.length > 0 && newPassword !== newPasswordConfirm}
              helperText={
                newPasswordConfirm.length > 0 && newPassword !== newPasswordConfirm
                  ? t('passwordsDoNotMatch')
                  : ' '
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showPasswordConfirm ? t('tooltipHidePassword') : t('tooltipShowPassword')}>
                      <IconButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} edge="end">
                        {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isChanging}
              startIcon={isChanging ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ fontWeight: 'bold', color: 'white' }}
            >
              {isChanging ? t('changingInProgress') : t('buttonChangePassword')}
            </Button>
          </form>
        </Box>
      )}

      <Box sx={{
        backgroundColor: tokens.surface, border: `1px solid ${tokens.line}`,
        borderLeft: `3px solid ${tokens.brand}`, borderRadius: 2, p: 2.5, mb: 3,
      }}>
        <Typography variant="body2" sx={{ color: tokens.inkMuted, whiteSpace: 'pre-line' }}
                    dangerouslySetInnerHTML={{ __html: t('warningText') }} />
        <Typography variant="body2" sx={{ color: tokens.inkMuted, whiteSpace: 'pre-line', mt: 1 }}
                    dangerouslySetInnerHTML={{ __html: t('warningText2') }} />
      </Box>

      {resultMessage && (
        <Box sx={{
            backgroundColor: resultMessage.startsWith('Hata:') ? '#f8d7da' : '#f1f8ff',
            p: 2,
            borderRadius: 2,
            mb: 4,
            border: resultMessage.startsWith('Hata:') ? '2px solid #f5c6cb' : '2px solid rgb(55, 56, 55)'
          }}>
          <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
            {t('resultTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'gray' }}>
            {resultMessage}
          </Typography>
        </Box>
      )}

      {/* Şifre değişimi AD ve Google'a sırayla gidiyor; birkaç saniye sürebilir.
          Ekranı kilitlemek kullanıcının tekrar tıklamasını da engelliyor. */}
      <Backdrop open={isChanging} sx={{ color: '#fff', zIndex: (th) => th.zIndex.drawer + 1 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
            {t('changingOverlayTitle')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('changingOverlayHint')}
          </Typography>
        </Box>
      </Backdrop>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast('')} severity="info" sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>

      <Box sx={{ textAlign: 'center', py: 2, mt: 4, borderTop: '1px solid #0c84cc' }}>
        <Typography variant="body2" sx={{ color: 'gray' }}>
          {t('footer')}
        </Typography>
      </Box>
    </Container>
  );
}

function SuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/");
    }, 5000);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <Container maxWidth="sm" sx={{ textAlign: 'center', pt: 4 }}>
      <Box sx={{ mb: 4 }}>
        <a href="https://sifre.lokmanhekim.edu.tr">
          <img
            src={process.env.PUBLIC_URL + '/assets/icon/logo.png'}
            alt={t('logoAlt')}
            style={{ width: '270px', marginBottom: '1rem' }}
          />
        </a>
      </Box>
      <Box sx={{
          border: '2px solid #28a745',
          borderRadius: 2,
          p: 2,
          backgroundColor: '#d4edda'
        }}>
        <Typography variant="h6" sx={{ color: '#155724' }}>
          {t('changePasswordSuccess')}
        </Typography>
      </Box>
    </Container>
  );
}

function App() {
  const { i18n } = useTranslation();

  // <html lang> sabit "tr" idi. CSS text-transform: uppercase dil kurallarını
  // uyguladığı için İngilizce "variety" kelimesi Türkçe kuralla "VARİETY"
  // oluyordu (i -> İ). Seçilen dile göre güncelleniyor.
  useEffect(() => {
    document.documentElement.lang = i18n.language || 'tr';
  }, [i18n.language]);

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/personel" element={<FormPage userType="personel" />} />
          <Route path="/ogrenci" element={<FormPage userType="ogrenci" />} />
          <Route path="/yetkili" element={<AuthorityPortal />} />
          <Route path="/success" element={<SuccessPage />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
